import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleUser, LogOut, UserCog } from 'lucide-react';
import type { MeResponse } from '@ucp/web/lib';
import { Sheet } from '../ui/Sheet';
import { ISSUE_REPORT_ICON } from './navIcons';
import { ReportIssueDialog } from './ReportIssueDialog';
import { profileSettingsSections } from '../account/profileSettingsSections';

/**
 * Header account menu (ADR-014 Amendment 7). A person-icon trigger that opens a
 * right-anchored glass drawer (shared `Sheet` primitive — native `<dialog>` focus
 * trap, Esc, focus return to the trigger; NF-10) consolidating the identity block and
 * sign-out that previously sat inline in the banner. Replaces the raw display-name span
 * + Sign out button, freeing header real estate and giving the account surface room to
 * grow (Account, Send feedback) without re-crowding the banner.
 *
 * No PII in the accessible name beyond the display name already shown in the banner
 * (SEC-8); the icon is decorative (`aria-hidden`).
 */
export function AccountMenu({ user, onSignOut }: { user: MeResponse; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const name = user.displayName ?? 'Account';
  const IssueReportIcon = ISSUE_REPORT_ICON;
  const issueReportEnabled = user.featureFlags.issueReportEnabled;
  // PRO-0740 / ADR-014 amendment (SF-3): surface the account-scoped Profile Settings
  // entry only when the flag is on AND at least one role-scoped section applies —
  // shares `profileSettingsSections` with the page so drawer visibility and page
  // content cannot drift (a no-section user would otherwise land on the empty state).
  const showProfileSettings =
    user.featureFlags.profileSettingsEnabled && profileSettingsSections(user).length > 0;

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Account menu"
        aria-expanded={open}
        aria-controls="account-menu"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md border border-border bg-workspace p-2 text-sm font-medium text-text-primary shadow-sm hover:bg-muted"
      >
        <CircleUser aria-hidden className="h-5 w-5 shrink-0 text-text-secondary" />
        <span className="hidden max-w-[12rem] truncate sm:inline">{name}</span>
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        returnFocusRef={triggerRef}
        anchor="right"
        tone="glass"
        id="account-menu"
        aria-labelledby="account-menu-title"
      >
        {/*
         * Clicking anywhere on the drawer that is not a menu option dismisses it
         * (as does the backdrop / main page area, handled by Sheet). Menu options
         * stop propagation and run their own action. Dismissal is also fully
         * keyboard-accessible via Esc (Sheet onCancel), so this static-element click
         * is a mouse convenience only; the keyboard path above is the accessibility
         * guarantee. The jsx-a11y rules are intentionally disabled on this one line.
         */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div className="flex h-full w-full flex-col" onClick={close}>
          <div className="flex items-center gap-3 border-b border-border px-4 py-4">
            <span
              aria-hidden
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
            >
              <CircleUser className="h-6 w-6" />
            </span>
            <div className="flex min-w-0 flex-col">
              <span
                id="account-menu-title"
                className="truncate text-base font-semibold text-text-primary"
              >
                {name}
              </span>
              {user.subject ? (
                <span className="truncate text-xs text-text-secondary">Signed in</span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
            {showProfileSettings ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen(false);
                  navigate('/account');
                }}
                className="inline-flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-text-primary hover:bg-muted"
              >
                <UserCog aria-hidden className="h-5 w-5 shrink-0 text-text-secondary" />
                Profile settings
              </button>
            ) : null}
            {issueReportEnabled ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen(false);
                  setReportOpen(true);
                }}
                className="inline-flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-text-primary hover:bg-muted"
              >
                <IssueReportIcon aria-hidden className="h-5 w-5 shrink-0 text-text-secondary" />
                Send feedback
              </button>
            ) : null}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                onSignOut();
              }}
              className="inline-flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-danger hover:bg-danger-bg"
            >
              <LogOut aria-hidden className="h-5 w-5 shrink-0" />
              Sign out
            </button>
          </div>
        </div>
      </Sheet>

      {issueReportEnabled ? (
        <ReportIssueDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          returnFocusRef={triggerRef}
        />
      ) : null}
    </>
  );
}
